import { useRef } from 'react';

// These are dashboard workspaces whose useful state lives inside the mounted
// page: filters, pagination, selection, expanded filter controls, and scroll.
// Keep only the most recently visited workspace mounted per dashboard tab.
// That covers the common list -> record -> back flow without retaining every
// route the user has ever visited (and all of its background work).
const PRESERVED_DASHBOARD_LIST_PATHS = new Set([
  '/',
  '/broadcasts',
  '/contacts',
  '/elements',
  '/forms',
  '/payments',
  '/payouts',
  '/segments',
  '/settings/imports',
  '/subscriptions',
]);

export const getDashboardListRouteCacheKey = (location) => {
  const pathname = location?.pathname || '/';
  return PRESERVED_DASHBOARD_LIST_PATHS.has(pathname) ? pathname : null;
};

/**
 * Preserve the most recent list workspace while the active dashboard tab shows
 * a record/detail route. PanelComponent is injected so this small cache stays
 * independent of the router implementation and can be regression-tested with
 * a stateful stand-in.
 */
export default function DashboardListRouteCache({
  PanelComponent,
  panelId,
  isActive,
  location,
  ...panelProps
}) {
  const currentListKey = getDashboardListRouteCacheKey(location);
  const preservedListRef = useRef(null);

  if (currentListKey) {
    if (preservedListRef.current?.key !== currentListKey) {
      // Visiting a different list replaces the old one, bounding the cache to
      // one retained workspace per dashboard tab.
      preservedListRef.current = {
        key: currentListKey,
        location,
      };
    } else {
      // Keep the newest history location/search/state while retaining the same
      // keyed React subtree and therefore its native component state.
      preservedListRef.current.location = location;
    }
  }

  const preservedList = preservedListRef.current;

  return (
    <>
      {preservedList ? (
        <PanelComponent
          key={`${panelId}:list:${preservedList.key}`}
          panelId={panelId}
          isActive={isActive && currentListKey === preservedList.key}
          location={preservedList.location}
          {...panelProps}
        />
      ) : null}

      {!currentListKey ? (
        <PanelComponent
          key={`${panelId}:current-route`}
          panelId={panelId}
          isActive={isActive}
          location={location}
          {...panelProps}
        />
      ) : null}
    </>
  );
}
